import * as React from "react";
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { FileText } from "lucide-react";

interface PaymentType {
  receiptNo: string;
  name: string;
  course: string;
  date: string;
  bCode: string;
  paid: number;
}

interface ReceiptProps {
  payment: PaymentType;
  totalFee: number;
  paidFee: number;
  pendingFee: number;
}

const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
  },
  container: {
    border: "1 solid #000000",
    padding: 20,
    margin: 10,
  },
  headerContainer: {
    borderBottom: "2 solid #000000",
    paddingBottom: 10,
    marginBottom: 20,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: "bold",
    textAlign: "center",
    color: "#000000",
    textTransform: "uppercase",
  },
  schoolInfo: {
    fontSize: 10,
    textAlign: "center",
    color: "#000000",
    marginTop: 5,
  },
  receiptHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    paddingHorizontal: 10,
  },
  receiptDetails: {
    fontSize: 10,
  },
  receiptTitle: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    marginVertical: 15,
    padding: 5,
    backgroundColor: "#f0f0f0",
  },
  studentDetails: {
    marginBottom: 20,
    padding: 10,
    backgroundColor: "#f9f9f9",
  },
  detailRow: {
    flexDirection: "row",
    marginBottom: 8,
  },
  detailLabel: {
    flex: 1,
    fontSize: 10,
    fontWeight: "bold",
  },
  detailValue: {
    flex: 2,
    fontSize: 10,
  },
  paymentTable: {
    marginTop: 20,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottom: "1 solid #000000",
    borderTop: "1 solid #000000",
    padding: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "0.5 solid #cccccc",
    padding: 8,
  },
  tableCell: {
    flex: 1,
    fontSize: 10,
    padding: 4,
  },
  tableCellAmount: {
    flex: 1,
    fontSize: 10,
    textAlign: "right",
    padding: 4,
  },
  totalRow: {
    flexDirection: "row",
    borderTop: "1 solid #000000",
    padding: 8,
    backgroundColor: "#f9f9f9",
  },
  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 50,
    paddingHorizontal: 20,
  },
  signatureBox: {
    width: 150,
  },
  signatureLine: {
    borderTop: "1 solid #000000",
  },
  signatureLabel: {
    fontSize: 8,
    textAlign: "center",
    marginTop: 5,
  },
  footer: {
    position: "absolute",
    bottom: 10,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 8,
    color: "#grey",
  },
  watermark: {
    position: "absolute",
    bottom: 50,
    fontSize: 8,
    left: 20,
    right: 0,
    color: "#666666",
    marginTop : 50
  },
});

const ReceiptDocument: React.FC<ReceiptProps> = ({
  payment,
  totalFee,
  paidFee,
  pendingFee,
}) => (
  <>
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.headerContainer}>
            <Text style={styles.headerTitle}>Robotwala</Text>
            <Text style={styles.schoolInfo}>
              201, Apollo Avenue, Old Palasia, Indore, M.P., India
            </Text>
            <Text style={styles.schoolInfo}>
              Phone: +91 740 084 4492 | Email: zero.robotwala@gmail.com
            </Text>
          </View>

          {/* Receipt Header */}
          <View style={styles.receiptHeader}>
            <View>
              <Text style={styles.receiptDetails}>
                Receipt No: {payment.receiptNo}
              </Text>
            </View>
            <View>
              <Text style={styles.receiptDetails}>
                Date: {new Date(payment.date).toLocaleDateString()}
              </Text>
            </View>
          </View>

          <Text style={styles.receiptTitle}>COURSE FEE RECEIPT</Text>

          {/* Student Details */}
          <View style={styles.studentDetails}>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Student Name:</Text>
              <Text style={styles.detailValue}>{payment.name}</Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Course:</Text>
              <Text style={styles.detailValue}>{payment.course}</Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Batch Code:</Text>
              <Text style={styles.detailValue}>{payment.bCode}</Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Payment Mode:</Text>
              <Text style={styles.detailValue}>
                {payment?.payMode?.toUpperCase()}
              </Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Total Course Fee:</Text>
              <Text style={styles.detailValue}>
                ₹{totalFee.toLocaleString()}
              </Text>
            </View>
          </View>

          {/* Payment Details Table */}
          <View style={styles.paymentTable}>
            <View style={styles.tableHeader}>
              <Text style={styles.tableCell}>Description</Text>
              <Text style={styles.tableCellAmount}>Amount (₹)</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>Remaining Fees</Text>
              <Text style={styles.tableCellAmount}>
                {(pendingFee + paidFee).toLocaleString()}
              </Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>Amount Paid</Text>
              <Text style={styles.tableCellAmount}>
                {paidFee.toLocaleString()}
              </Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={[styles.tableCell, { fontWeight: "bold" }]}>
                Balance Due
              </Text>
              <Text style={[styles.tableCellAmount, { fontWeight: "bold" }]}>
                {pendingFee.toLocaleString()}
              </Text>
            </View>
          </View>

          {/* Signature Section */}
          <View style={styles.signatureSection}>
            <View style={styles.signatureBox}></View>
            <View style={styles.signatureBox}></View>
          </View>

          {/* Footer */}
          <Text style={styles.watermark}>
            This Is A System-generated Receipt No Need To Seal & Sign
          </Text>

          <Text style={styles.footer}>Thank you for choosing E-Skills Web</Text>
        </View>
      </Page>
    </Document>
  </>
);

const ReceiptExportButton: React.FC<ReceiptProps> = (props) => {
  const [isClient, setIsClient] = React.useState(false);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient) {
    return (
      <button
        className="text-blue-600 hover:text-blue-700 transition-colors disabled:text-gray-400"
        disabled
      >
        <FileText size={20} />
      </button>
    );
  }

 

  return (
    <PDFDownloadLink
      document={<ReceiptDocument {...props} />}
      fileName={`Receipt-${props.payment.receiptNo}.pdf`}
    >
      <button
        className="text-blue-600 hover:text-blue-700 transition-colors disabled:text-gray-400"
        disabled={props.payment.paid === 0}
        title="Download Receipt"
      >

        <FileText size={20} />
      </button>
    </PDFDownloadLink>
  );
};

export default ReceiptExportButton;
